import { useCallback } from "react";

import useAuth from "$src/core/hooks/use-auth";
import type { HexColorDto, ProfileDto } from "$src/core/models/dto";
import useCreateProfileMutation from "$src/core/mutations/use-create-profile-mutation";
import useDeleteProfileMutation from "$src/core/mutations/use-delete-profile-mutation";
import useUpdateProfileMutation from "$src/core/mutations/use-update-profile-mutation";
import useProfilesQuery from "$src/core/queries/use-profiles-query";

const useProfiles = () => {
  const { profileId, deselectProfile } = useAuth();

  const { data: profiles, status, error, refetch } = useProfilesQuery();

  const { mutateAsync: create, isPending: isAdding } =
    useCreateProfileMutation();
  const { mutateAsync: update, isPending: isUpdating } =
    useUpdateProfileMutation();
  const { mutateAsync: remove, isPending: isDeleting } =
    useDeleteProfileMutation();

  const addProfile = useCallback(
    (name: string, tint: HexColorDto) => create({ name, tint }),
    [create],
  );

  const updateProfile = useCallback(
    (id: string, name: string, tint: HexColorDto) => update({ id, name, tint }),
    [update],
  );

  const deleteProfile = useCallback(
    async (profile: ProfileDto) => {
      await remove({ id: profile.id });

      // A deleted profile must not stay selected, or nothing resolves it.
      if (profileId === profile.id) deselectProfile();
    },
    [remove, profileId, deselectProfile],
  );

  return {
    profiles,
    status,
    error,
    refetch,
    addProfile,
    updateProfile,
    deleteProfile,
    isAdding,
    isUpdating,
    isDeleting,
  };
};

export default useProfiles;
